import { Module } from "vuex";
import { AutonomousFormAdditionalCheckboxService } from "../../services/AutonomousFormAdditionalCheckboxService";
import { RootState } from "../RootState";
import { FlowAdditionalCheckboxUpdateRequest } from "../../models/requests/FlowAdditionalCheckboxUpdateRequest";
import { AutonomousFormAdditionalCheckbox } from "../../models/responses/AutonomousFormAdditionalCheckbox";
import { FlowBaseCheckboxCreateRequestImpl } from "../../models/requests/FlowBaseCheckboxCreateRequest";

export interface AutonomousFormAdditionalCheckboxState {
  additionalCheckboxes: AutonomousFormAdditionalCheckbox[];
}

// Magasin VueX qui coordonne l'échange d'infos entre les différentes vues.
export const autonomousFormAdditionalCheckboxModule: Module<
  AutonomousFormAdditionalCheckboxState,
  RootState
> = {
  namespaced: true,
  // Données partagées du magasin (ne peuvent pas être modifiées directement)
  state: {
    additionalCheckboxes: []
  },

  // Accesseurs pour propriétés calculées
  getters: {
    orders(state) {
      return state.additionalCheckboxes.map(checkbox => checkbox.order);
    },
    maxOrder(state, getters) {
      return getters.orders.reduce(
        (a: number, b: number) => Math.max(a, b),
        -1
      );
    }
  },

  // Opérations de modification des données
  mutations: {
    /**
     * Updates autonomousFormBaseCheckboxes
     */
    setAdditionalCheckboxes(state, value: AutonomousFormAdditionalCheckbox[]) {
      state.additionalCheckboxes = value;
    },
    /**
     * Deletes an additional Checkbox
     * @param state
     * @param checkboxId
     */
    deleteAdditionalCheckbox(state, checkboxId: number) {
      const index = state.additionalCheckboxes.findIndex(
        val => val.id === checkboxId
      );
      if (index > -1) {
        state.additionalCheckboxes.splice(index, 1);
      }
    },

    /**
     * Adds or update an additional Checkbox
     * @param state
     * @param value
     */
    addOrUpdateAdditionalCheckbox(
      state,
      value: AutonomousFormAdditionalCheckbox
    ) {
      const index = state.additionalCheckboxes.findIndex(
        val => val.id === value.id
      );
      if (index > -1) {
        state.additionalCheckboxes.splice(index, 1, value);
      } else {
        state.additionalCheckboxes.push(value);
      }
    }
  },

  // Actions pouvant être appelées par les vues pour agir sur le magasin
  actions: {
    async loadCheckbox(
      { commit },
      value: { checkboxId: number; flowId: number }
    ) {
      const response = await AutonomousFormAdditionalCheckboxService.getCheckbox(
        value.checkboxId,
        value.flowId
      );
      commit("addOrUpdateAdditionalCheckbox", response.data);
      return response.data;
    },

    async deleteCheckbox(
      { commit },
      value: { checkboxId: number; flowId: number }
    ) {
      const response = await AutonomousFormAdditionalCheckboxService.deleteCheckbox(
        value.checkboxId,
        value.flowId
      );
      commit("deleteAdditionalCheckbox", response.data?.id);
      return response.data;
    },

    async setCheckbox(
      context,
      value: {
        checkboxId: number;
        flowId: number;
        checkbox: FlowAdditionalCheckboxUpdateRequest;
      }
    ) {
      const response = await AutonomousFormAdditionalCheckboxService.setCheckbox(
        value.checkboxId,
        value.flowId,
        value.checkbox
      );
      context.commit("addOrUpdateAdditionalCheckbox", response.data);
      return response.data;
    },

    async createCheckbox(
      context,
      value: { flowId: number; checkbox: FlowBaseCheckboxCreateRequestImpl }
    ) {
      const response = await AutonomousFormAdditionalCheckboxService.createCheckbox(
        value.flowId,
        value.checkbox
      );
      context.commit("addOrUpdateAdditionalCheckbox", response.data);
    },

    async createDefaultCheckbox(context, value: { flowId: number }) {
      const checkbox = new FlowBaseCheckboxCreateRequestImpl(value.flowId);
      const response = await AutonomousFormAdditionalCheckboxService.createCheckbox(
        value.flowId,
        checkbox
      );
      context.commit("addOrUpdateAdditionalCheckbox", response.data);
    },

    async loadAllAdditionalCheckboxes(context, flowId: number) {
      const results = await AutonomousFormAdditionalCheckboxService.getCheckboxes(
        flowId
      );
      context.commit("setAdditionalCheckboxes", results.data);
      return results;
    }
  }
};
